import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import { GlobalDataStore } from "@/lib/mock-data/global-data-store";
import { differenceInDays, parseISO, isAfter, isBefore, addDays } from "date-fns";
import type { DisputeDashboardApiResponse } from "@/types/api/disputes";
import type { DisputeUI } from "@/types/ui/disputes";

export interface DashboardPriorityBreakdown {
  critical: { count: number; amount: number };
  high: { count: number; amount: number };
  medium: { count: number; amount: number };
  low: { count: number; amount: number };
}

export interface DashboardReasonBreakdown {
  reason: string;
  count: number;
  percentage: number;
}

export interface DashboardRecentDispute {
  id: string;
  customer: string;
  type: string;
  amount: number;
  currencyCode?: string;
  status: string;
  priority: string;
  assignedTo: string;
  createdDate: string;
  dueDate: string;
  currentLevel: number;
  totalLevels: number;
  closedReason?: string | null;
  daysOpen: number;
}

export interface DashboardAssignee {
  name: string;
  count: number;
  amount: number;
}

export interface DashboardMetrics {
  activeCount: number;
  totalAmount: number;
  avgResolutionTime: number;
  resolutionRate: number;
  byPriority: DashboardPriorityBreakdown;
  byReason: DashboardReasonBreakdown[];
  recentDisputes: DashboardRecentDispute[];
  overdueCount: number;
  dueSoonCount: number;
  agingBuckets: Record<string, number>;
  avgWorkflowProgress: number;
  topAssignees: DashboardAssignee[];
}

function mapApiResponseToMetrics(response: DisputeDashboardApiResponse): DashboardMetrics {
  const totalDisputeCount = response.totalDisputeCount;
  const resolutionRate = totalDisputeCount > 0
    ? Math.round((response.resolvedClosedCount / totalDisputeCount) * 100)
    : 0;

  const priorityMap: DashboardPriorityBreakdown = {
    critical: { count: 0, amount: 0 },
    high: { count: 0, amount: 0 },
    medium: { count: 0, amount: 0 },
    low: { count: 0, amount: 0 },
  };

  for (const p of response.byPriority) {
    const key = p.priorityCode.toLowerCase() as keyof DashboardPriorityBreakdown;
    if (key in priorityMap) {
      priorityMap[key] = { count: p.disputeCount, amount: p.disputeAmount };
    }
  }

  const totalActiveForPercentage = response.activeDisputeCount || 1;
  const byReason: DashboardReasonBreakdown[] = response.byType.map(x => ({
    reason: x.disputeTypeName,
    count: x.disputeCount,
    percentage: Math.round((x.disputeCount / totalActiveForPercentage) * 100),
  }));

  const today = new Date();
  const recentDisputes: DashboardRecentDispute[] = response.priorityDisputes.map(d => {
    let daysOpen = 0;
    try {
      daysOpen = differenceInDays(today, parseISO(d.createdDate));
    } catch {
      // ignore
    }
    return {
      id: d.disputeNumber ?? d.id,
      customer: d.customer,
      type: d.type,
      amount: d.amount,
      currencyCode: d.currencyCode ?? undefined,
      status: d.status,
      priority: d.priority,
      assignedTo: d.assignedTo ?? "",
      createdDate: d.createdDate,
      dueDate: d.dueDate ?? "",
      currentLevel: d.currentLevel,
      totalLevels: d.totalLevels,
      closedReason: d.closedReason,
      daysOpen,
    };
  });

  const topAssignees: DashboardAssignee[] = response.byAssignee.map(x => ({
    name: x.assignedToUserName ?? "Unassigned",
    count: x.disputeCount,
    amount: x.disputeAmount,
  }));

  return {
    activeCount: response.activeDisputeCount,
    totalAmount: response.activeDisputeAmount,
    avgResolutionTime: response.avgResolutionDays != null ? Math.round(response.avgResolutionDays) : 0,
    resolutionRate,
    byPriority: priorityMap,
    byReason,
    recentDisputes,
    overdueCount: response.overdueCount,
    dueSoonCount: response.dueSoonCount,
    agingBuckets: {
      '0-7': response.agingBuckets.aging0to7,
      '8-14': response.agingBuckets.aging8to14,
      '15-30': response.agingBuckets.aging15to30,
      '31+': response.agingBuckets.aging31Plus,
    },
    avgWorkflowProgress: response.avgWorkflowProgress != null ? Math.round(response.avgWorkflowProgress) : 0,
    topAssignees,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function convertMockDisputeToUI(mockDispute: any): DisputeUI {
  return {
    id: mockDispute.id,
    numericId: mockDispute.numericId,
    customer: mockDispute.customer,
    customerId: parseInt(mockDispute.customerId),
    customerAccountCode: mockDispute.customerAccountCode || mockDispute.id.replace('DSP', 'CUS'),
    contactName: mockDispute.contactName || "Unknown Contact",
    contactEmail: mockDispute.contactEmail || "contact@example.com",
    contactPhone: mockDispute.contactPhone || "+44 20 7000 0000",
    type: mockDispute.type,
    amount: mockDispute.amount,
    status: mockDispute.status,
    priority: mockDispute.priority,
    assignedTo: mockDispute.assignedTo,
    createdDate: mockDispute.createdDate instanceof Date
      ? mockDispute.createdDate.toISOString().split('T')[0]
      : mockDispute.createdDate,
    dueDate: mockDispute.dueDate instanceof Date
      ? mockDispute.dueDate.toISOString().split('T')[0]
      : mockDispute.dueDate,
    workflow: mockDispute.workflow,
    aiScore: mockDispute.aiScore,
    aiRecommendation: mockDispute.aiRecommendation,
    currentLevel: mockDispute.currentLevel,
    totalLevels: mockDispute.totalLevels,
    description: mockDispute.description,
    resolutionNotes: mockDispute.resolutionNotes,
    resolutionOutcome: mockDispute.resolutionOutcome,
    resolutionAmount: mockDispute.resolutionAmount,
    resolutionDate: mockDispute.resolutionDate,
    resolvedBy: mockDispute.resolvedBy,
    resolutionDetails: mockDispute.resolutionDetails,
    closedReason: mockDispute.closedReason,
    closedDate: mockDispute.closedDate,
    closedNote: mockDispute.closedNote,
    transactions: [],
  };
}

function computeMockDashboardMetrics(filterName?: string): DashboardMetrics {
  const store = GlobalDataStore.getInstance();
  const allDisputes = store.getDisputes().map(convertMockDisputeToUI);

  const filtered = filterName && filterName !== "everyone"
    ? allDisputes.filter(x => x.assignedTo === filterName)
    : allDisputes;

  const activeDisputes = filtered.filter(x =>
    x.status !== "Resolved" && x.status !== "Closed"
  );

  const totalAmount = activeDisputes.reduce((sum, x) => sum + x.amount, 0);

  const byPriority: DashboardPriorityBreakdown = {
    critical: {
      count: activeDisputes.filter(x => x.priority === "Critical").length,
      amount: activeDisputes.filter(x => x.priority === "Critical").reduce((sum, x) => sum + x.amount, 0),
    },
    high: {
      count: activeDisputes.filter(x => x.priority === "High").length,
      amount: activeDisputes.filter(x => x.priority === "High").reduce((sum, x) => sum + x.amount, 0),
    },
    medium: {
      count: activeDisputes.filter(x => x.priority === "Medium").length,
      amount: activeDisputes.filter(x => x.priority === "Medium").reduce((sum, x) => sum + x.amount, 0),
    },
    low: {
      count: activeDisputes.filter(x => x.priority === "Low").length,
      amount: activeDisputes.filter(x => x.priority === "Low").reduce((sum, x) => sum + x.amount, 0),
    },
  };

  const reasonCounts: Record<string, number> = {};
  for (const d of activeDisputes) {
    reasonCounts[d.type] = (reasonCounts[d.type] || 0) + 1;
  }
  const byReason = Object.entries(reasonCounts)
    .map(([reason, count]) => ({
      reason,
      count,
      percentage: Math.round((count / (activeDisputes.length || 1)) * 100),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const resolvedDisputes = filtered.filter(x =>
    x.status === "Resolved" || x.status === "Closed"
  );

  const today = new Date();
  let avgResolutionTime = 0;
  if (resolvedDisputes.length > 0) {
    const totalDays = resolvedDisputes.reduce((sum, x) => {
      try {
        return sum + differenceInDays(today, parseISO(x.createdDate));
      } catch {
        return sum;
      }
    }, 0);
    avgResolutionTime = Math.round(totalDays / resolvedDisputes.length);
  }

  const resolutionRate = filtered.length > 0
    ? Math.round((resolvedDisputes.length / filtered.length) * 100)
    : 0;

  const overdueCount = activeDisputes.filter(x => {
    try {
      return isBefore(parseISO(x.dueDate), today);
    } catch {
      return false;
    }
  }).length;

  const dueSoonCount = activeDisputes.filter(x => {
    try {
      const dueDate = parseISO(x.dueDate);
      return isAfter(dueDate, today) && isBefore(dueDate, addDays(today, 3));
    } catch {
      return false;
    }
  }).length;

  const agingBuckets: Record<string, number> = { '0-7': 0, '8-14': 0, '15-30': 0, '31+': 0 };
  for (const d of activeDisputes) {
    try {
      const days = differenceInDays(today, parseISO(d.createdDate));
      if (days <= 7) agingBuckets['0-7']++;
      else if (days <= 14) agingBuckets['8-14']++;
      else if (days <= 30) agingBuckets['15-30']++;
      else agingBuckets['31+']++;
    } catch {
      // skip
    }
  }

  const recentDisputes: DashboardRecentDispute[] = [...activeDisputes]
    .sort((a, b) => {
      const priorityOrder: Record<string, number> = { Critical: 0, High: 1, Medium: 2, Low: 3 };
      const ap = priorityOrder[a.priority] ?? 4;
      const bp = priorityOrder[b.priority] ?? 4;
      if (ap !== bp) return ap - bp;
      try {
        return parseISO(b.createdDate).getTime() - parseISO(a.createdDate).getTime();
      } catch {
        return 0;
      }
    })
    .slice(0, 10)
    .map(x => {
      let daysOpen = 0;
      try {
        daysOpen = differenceInDays(today, parseISO(x.createdDate));
      } catch {
        // ignore
      }
      return {
        id: x.id,
        customer: x.customer,
        type: x.type,
        amount: x.amount,
        currencyCode: x.currencyCode,
        status: x.status,
        priority: x.priority,
        assignedTo: x.assignedTo,
        createdDate: x.createdDate,
        dueDate: x.dueDate,
        currentLevel: x.currentLevel,
        totalLevels: x.totalLevels,
        closedReason: x.closedReason,
        daysOpen,
      };
    });

  const workflowMetrics = activeDisputes.map(x => ({
    progress: x.totalLevels > 0 ? (x.currentLevel / x.totalLevels) * 100 : 0,
  }));
  const avgWorkflowProgress = workflowMetrics.length > 0
    ? Math.round(workflowMetrics.reduce((sum, x) => sum + x.progress, 0) / workflowMetrics.length)
    : 0;

  const assigneeWorkload: Record<string, { count: number; amount: number }> = {};
  for (const d of activeDisputes) {
    if (!assigneeWorkload[d.assignedTo]) {
      assigneeWorkload[d.assignedTo] = { count: 0, amount: 0 };
    }
    assigneeWorkload[d.assignedTo].count++;
    assigneeWorkload[d.assignedTo].amount += d.amount;
  }
  const topAssignees = Object.entries(assigneeWorkload)
    .map(([name, data]) => ({ name, ...data }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  return {
    activeCount: activeDisputes.length,
    totalAmount,
    avgResolutionTime,
    resolutionRate,
    byPriority,
    byReason,
    recentDisputes,
    overdueCount,
    dueSoonCount,
    agingBuckets,
    avgWorkflowProgress,
    topAssignees,
  };
}

export interface UseDisputeDashboardDataParams {
  assignedToUserId?: number;
  filterName?: string;
}

export function useDisputeDashboardData(params?: UseDisputeDashboardDataParams) {
  const { apiGet } = useApi();

  const mockMetrics = computeMockDashboardMetrics(params?.filterName);

  return useModeAwareQuery<DashboardMetrics, Error>(
    ["dispute-dashboard-data", params?.assignedToUserId, params?.filterName],
    async ({ signal }) => {
      const queryParams: Record<string, string | number | boolean | undefined> = {};
      if (params?.assignedToUserId) {
        queryParams.assignedToUserId = params.assignedToUserId;
      }
      const response = await apiGet<DisputeDashboardApiResponse>(
        "/api/v1/disputes/dashboard",
        queryParams,
        signal,
      );
      return mapApiResponseToMetrics(response);
    },
    mockMetrics,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}
